import React, { useState, useEffect, useRef, useCallback } from 'react';

const POLL_INTERVAL = 10000; // 10 seconds

const EstimationQueuePanel = ({ onComplete, onProgress }) => {
  const [queueData, setQueueData] = useState({ in_progress: [], pending: [], summary: null });
  const [visible, setVisible] = useState(false);
  const pollRef = useRef(null);
  const prevQueueDataRef = useRef({ in_progress: [], pending: [] });

  const fetchQueueStatus = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/attendee_value_estimates/queue_status', {
        headers: { 'Accept': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        const prevData = prevQueueDataRef.current;
        const hadItems = (prevData.in_progress?.length > 0) || (prevData.pending?.length > 0);
        const hasItems = (data.in_progress?.length > 0) || (data.pending?.length > 0);
        const hasFailures = (data.recently_failed?.length > 0);

        setQueueData(data);
        setVisible(hasItems || hasFailures);
        prevQueueDataRef.current = data;

        if (hadItems && !hasItems && onComplete) {
          onComplete();
        } else if (hasItems && onProgress) {
          onProgress();
        }
      }
    } catch (err) {
      console.error('[EstimationQueuePanel] Error fetching queue status:', err);
    }
  }, [onComplete, onProgress]);

  useEffect(() => {
    fetchQueueStatus();
    pollRef.current = setInterval(fetchQueueStatus, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [fetchQueueStatus]);

  const refresh = useCallback(() => {
    fetchQueueStatus();
  }, [fetchQueueStatus]);

  useEffect(() => {
    window.__estimationQueueRefresh = refresh;
    return () => { delete window.__estimationQueueRefresh; };
  }, [refresh]);

  if (!visible) return null;

  const { in_progress, pending, recently_failed, summary } = queueData;

  return (
    <div className="estimation-queue-panel">
      <div className="estimation-queue-panel__header">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="estimation-queue-panel__icon">
          <path d="M8 2L9.67 5.81L14 6.27L10.9 8.89L11.82 13L8 11.09L4.18 13L5.1 8.89L2 6.27L6.33 5.81L8 2Z"
            stroke="#fbbf24" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="#fbbf24"/>
        </svg>
        <span className="estimation-queue-panel__title">Attendee Value Estimation Queue</span>
        {summary && (
          <span className="estimation-queue-panel__summary">
            {in_progress.length} running, {pending.length} queued
          </span>
        )}
      </div>

      {in_progress.map(item => (
        <RunningItem key={item.id} item={item} />
      ))}

      {pending.length > 0 && (
        <div className="estimation-queue-panel__pending-section">
          <div className="estimation-queue-panel__pending-label">QUEUED</div>
          {pending.map((item, idx) => (
            <PendingItem key={item.id} item={item} position={idx + 1} />
          ))}
        </div>
      )}

      {recently_failed?.length > 0 && (
        <div className="estimation-queue-panel__failed-section">
          <div className="estimation-queue-panel__failed-label">FAILED</div>
          {recently_failed.map(item => (
            <FailedItem key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
};

const RunningItem = ({ item }) => {
  const [elapsed, setElapsed] = useState(item.elapsed_seconds || 0);

  useEffect(() => {
    setElapsed(item.elapsed_seconds || 0);
    const timer = setInterval(() => {
      setElapsed(prev => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [item.id, item.status]); // Reset timer on identity change or stage transition

  const formatElapsed = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s.toString().padStart(2, '0')}s`;
  };

  return (
    <div className="estimation-queue-item estimation-queue-item--running">
      <div className="estimation-queue-item__main">
        <div className="estimation-queue-item__status-indicator">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" strokeDasharray="31.416" strokeLinecap="round" opacity="0.3"/>
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" strokeDasharray="15.708" strokeLinecap="round">
              <animate attributeName="stroke-dashoffset" values="0;-62.83" dur="1s" repeatCount="indefinite"/>
            </circle>
          </svg>
        </div>
        <div className="estimation-queue-item__info">
          <div className="estimation-queue-item__event-name">
            {item.event_name || 'Unknown Event'}
            <span className="estimation-queue-item__company-name">{item.company_name}</span>
          </div>
          <div className="estimation-queue-item__details">
            <span className="estimation-queue-item__stage">{item.stage_label}</span>
            <span className="estimation-queue-item__separator">|</span>
            <span className="estimation-queue-item__elapsed">{formatElapsed(elapsed)}</span>
            {item.estimated_cost && item.estimated_cost !== '$0.00' && (
              <>
                <span className="estimation-queue-item__separator">|</span>
                <span className="estimation-queue-item__cost">{item.estimated_cost}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const PendingItem = ({ item, position }) => {
  return (
    <div className="estimation-queue-item estimation-queue-item--pending">
      <div className="estimation-queue-item__main">
        <div className="estimation-queue-item__position">{position}</div>
        <div className="estimation-queue-item__info">
          <div className="estimation-queue-item__event-name">
            {item.event_name || 'Unknown Event'}
            <span className="estimation-queue-item__company-name">{item.company_name}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const FailedItem = ({ item }) => {
  return (
    <div className="estimation-queue-item estimation-queue-item--failed">
      <div className="estimation-queue-item__main">
        <div className="estimation-queue-item__fail-icon">!</div>
        <div className="estimation-queue-item__info">
          <div className="estimation-queue-item__event-name">
            {item.event_name || 'Unknown Event'}
            <span className="estimation-queue-item__company-name">{item.company_name}</span>
          </div>
          <div className="estimation-queue-item__error">{item.error_message || 'Unknown error'}</div>
        </div>
      </div>
    </div>
  );
};

export default EstimationQueuePanel;
